/**
 * Copiloto de call — insights.
 *
 * O que a equipe ouve nas calls (objecoes por tipo), o que o playbook
 * responde bem (ranking por "usei"), quanto o copiloto demora, e a
 * transcricao de cada call com os cards no ponto em que apareceram.
 */
import { useState } from "react";
import { Link } from "wouter";
import { trpc } from "@/lib/trpc";
import { PermissionGuard } from "@/components/PermissionGuard";
import DashboardLayout from "@/components/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { tempoRelativo } from "@/lib/tempo";
import { Settings2 } from "lucide-react";

const ROTULO_SUBTIPO: Record<string, string> = {
  preco: "Preço", timing: "Timing", autoridade: "Alçada", concorrente: "Concorrente", ceticismo: "Ceticismo", escopo: "Escopo", nenhum: "Sinal de compra",
};
const ROTULO_FEEDBACK: Record<string, { label: string; cls: string }> = {
  usei: { label: "Usei", cls: "bg-green-100 text-green-800" },
  copiou: { label: "Copiou", cls: "bg-blue-100 text-blue-800" },
  descartou: { label: "Descartou", cls: "bg-slate-100 text-slate-700" },
};

function ms(v: number | null | undefined) {
  return v == null ? "—" : `${(v / 1000).toFixed(1)} s`;
}
function pct(parte: number, total: number) {
  return total ? `${Math.round((parte / total) * 100)}%` : "—";
}
function relogio(tMs: number) {
  const s = Math.floor(tMs / 1000);
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

function Kpi({ rotulo, valor, ajuda }: { rotulo: string; valor: string | number; ajuda?: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{rotulo}</p>
        <p className="mt-1 text-2xl font-semibold">{valor}</p>
        {ajuda && <p className="text-xs text-muted-foreground">{ajuda}</p>}
      </CardContent>
    </Card>
  );
}

// ─── Detalhe de uma call ─────────────────────────────────────────────────────

function DetalheCall({ id, onFechar }: { id: number; onFechar: () => void }) {
  const q = trpc.copiloto.sessoes.get.useQuery({ id });
  const d = q.data;

  // Linha do tempo: falas, eventos e sugestoes intercalados pela posicao na call.
  type Item = { tMs: number; ordem: number; node: React.ReactNode };
  const itens: Item[] = [];
  if (d) {
    for (const f of d.falas) {
      itens.push({
        tMs: f.tMs, ordem: 0,
        node: (
          <div key={`f${f.id}`} className={`text-sm ${f.canal === "lead" ? "" : "text-muted-foreground"}`}>
            <span className="mr-2 font-mono text-[11px] text-muted-foreground">{relogio(f.tMs)}</span>
            <span className="mr-1 font-medium">{f.canal === "lead" ? "Lead:" : "Você:"}</span>{f.texto}
          </div>
        ),
      });
    }
    for (const e of d.eventos) {
      itens.push({
        tMs: e.tMs, ordem: 1,
        node: (
          <div key={`e${e.id}`} className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-900">
            Detectado: {e.tipo} / {ROTULO_SUBTIPO[e.subtipo] ?? e.subtipo} (confiança {e.confianca})
          </div>
        ),
      });
    }
    for (const s of d.sugestoes) {
      const ev = d.eventos.find((e) => e.id === s.eventId);
      const fb = s.feedback ? ROTULO_FEEDBACK[s.feedback] : null;
      itens.push({
        tMs: ev?.tMs ?? 0, ordem: 2,
        node: (
          <div key={`s${s.id}`} className="rounded-md border bg-card p-3 text-sm shadow-sm">
            <div className="mb-1 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
              <span>Sugestão{s.pedidoManual ? " (socorro)" : ""} · {ms(s.latenciaDesdeFalaMs ?? s.latenciaMs)} após a fala</span>
              {s.semCobertura && <Badge variant="outline">sem cobertura</Badge>}
              {fb && <span className={`rounded px-1.5 py-0.5 font-medium ${fb.cls}`}>{fb.label}</span>}
            </div>
            <p>{s.principal}</p>
            {s.proximaPergunta && <p className="mt-1 text-xs text-muted-foreground">Depois pergunte: {s.proximaPergunta}</p>}
            {s.ancora && <p className="mt-1 text-[11px] text-muted-foreground">fonte: {s.ancora}</p>}
          </div>
        ),
      });
    }
    itens.sort((a, b) => a.tMs - b.tMs || a.ordem - b.ordem);
  }

  return (
    <Sheet open onOpenChange={(o) => !o && onFechar()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>Call #{id}{d?.usuario ? ` — ${d.usuario}` : ""}</SheetTitle>
          <SheetDescription>
            {d ? `${new Date(d.startedAt).toLocaleString("pt-BR")} · ${d.plataforma}${d.estagioFinal ? ` · terminou em ${d.estagioFinal}` : ""}` : "Carregando…"}
          </SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-2">
          {d && itens.length === 0 && <p className="text-sm text-muted-foreground">Nenhuma fala registrada nesta call.</p>}
          {itens.map((i) => i.node)}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Pagina ──────────────────────────────────────────────────────────────────

function CopilotoInsightsContent() {
  const [dias, setDias] = useState(30);
  const [userId, setUserId] = useState<number | undefined>(undefined);
  const [aberta, setAberta] = useState<number | null>(null);

  const ins = trpc.copiloto.insights.useQuery({ dias, userId }, { staleTime: 30_000 });
  const sessoes = trpc.copiloto.sessoes.list.useQuery({ dias, userId, limit: 100 }, { staleTime: 30_000 });
  const d = ins.data;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">Insights do copiloto</h1>
          <p className="mt-1 text-muted-foreground">{d?.equipe ? "Calls de toda a equipe." : "Suas calls."} O clique em "Usei" é o que diz qual trecho do playbook funciona.</p>
        </div>
        <div className="flex gap-2">
          <Select value={String(dias)} onValueChange={(v) => setDias(Number(v))}>
            <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Últimos 7 dias</SelectItem>
              <SelectItem value="30">Últimos 30 dias</SelectItem>
              <SelectItem value="90">Últimos 90 dias</SelectItem>
            </SelectContent>
          </Select>
          {d?.equipe && d.porVendedor.length > 0 && (
            <Select value={userId ? String(userId) : "todos"} onValueChange={(v) => setUserId(v === "todos" ? undefined : Number(v))}>
              <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Toda a equipe</SelectItem>
                {d.porVendedor.map((v) => <SelectItem key={v.userId} value={String(v.userId)}>{v.usuario ?? `Usuário ${v.userId}`}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button asChild variant="outline"><Link href="/copiloto"><Settings2 className="mr-2 h-4 w-4" /> Configurações</Link></Button>
        </div>
      </div>

      {d && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Kpi rotulo="Calls" valor={d.totalSessoes} ajuda={`${d.minutosEmCall} min com o copiloto ligado`} />
          <Kpi rotulo="Sugestões" valor={d.totalSugestoes} ajuda={`${d.totalEventos} momentos detectados`} />
          <Kpi rotulo="Taxa de uso" valor={pct(d.feedback.usei, d.totalSugestoes)} ajuda={`${d.feedback.usei} usei · ${d.feedback.copiou} copiou · ${d.feedback.descartou} descartou`} />
          <Kpi rotulo="Latência fala → card" valor={ms(d.latencia.p50)} ajuda={`p95 ${ms(d.latencia.p95)} · meta 4 s · ${d.semCobertura} sem cobertura`} />
        </div>
      )}

      {d && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Objeções mais ouvidas</CardTitle></CardHeader>
            <CardContent>
              {d.porSubtipo.length === 0 && <p className="text-sm text-muted-foreground">Nada detectado no período.</p>}
              <Table>
                <TableBody>
                  {d.porSubtipo.map((r) => (
                    <TableRow key={r.subtipo}>
                      <TableCell className="font-medium">{ROTULO_SUBTIPO[r.subtipo] ?? r.subtipo}</TableCell>
                      <TableCell className="text-right">{r.total}</TableCell>
                      <TableCell className="text-right text-xs text-muted-foreground">{r.usei} usei</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Trechos do playbook que mais funcionam</CardTitle></CardHeader>
            <CardContent>
              {d.porAncora.length === 0 && <p className="text-sm text-muted-foreground">Nenhuma sugestão ancorada no playbook ainda.</p>}
              <Table>
                <TableBody>
                  {d.porAncora.map((r) => (
                    <TableRow key={r.ancora}>
                      <TableCell className="font-medium">{r.ancora}</TableCell>
                      <TableCell className="text-right">{r.total}×</TableCell>
                      <TableCell className="text-right text-xs">
                        <span className="text-green-700">{pct(r.usei, r.total)} usei</span>
                        {r.descartou > 0 && <span className="ml-2 text-muted-foreground">{r.descartou} descartou</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}

      {d?.equipe && d.porVendedor.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Por vendedor</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow><TableHead>Vendedor</TableHead><TableHead className="text-right">Calls</TableHead><TableHead className="text-right">Sugestões</TableHead><TableHead className="text-right">Usou</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {d.porVendedor.map((v) => (
                  <TableRow key={v.userId}>
                    <TableCell>{v.usuario ?? `Usuário ${v.userId}`}</TableCell>
                    <TableCell className="text-right">{v.sessoes}</TableCell>
                    <TableCell className="text-right">{v.sugestoes}</TableCell>
                    <TableCell className="text-right">{pct(v.usei, v.sugestoes)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Calls</CardTitle></CardHeader>
        <CardContent>
          {sessoes.data?.length === 0 && <p className="text-sm text-muted-foreground">Nenhuma call no período. Conecte a extensão e inicie na aba da reunião.</p>}
          {sessoes.data && sessoes.data.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Quando</TableHead>
                  {d?.equipe && <TableHead>Vendedor</TableHead>}
                  <TableHead>Plataforma</TableHead>
                  <TableHead className="text-right">Falas</TableHead>
                  <TableHead className="text-right">Sugestões</TableHead>
                  <TableHead>Estágio final</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {sessoes.data.map((s) => (
                  <TableRow key={s.id} className="cursor-pointer" onClick={() => setAberta(s.id)}>
                    <TableCell>{tempoRelativo(s.startedAt)}{!s.endedAt && <Badge variant="outline" className="ml-2 text-[10px]">em andamento</Badge>}</TableCell>
                    {d?.equipe && <TableCell>{s.usuario ?? "—"}</TableCell>}
                    <TableCell>{s.plataforma}</TableCell>
                    <TableCell className="text-right">{s.totalFalas}</TableCell>
                    <TableCell className="text-right">{s.totalSugestoes}</TableCell>
                    <TableCell>{s.estagioFinal ?? "—"}</TableCell>
                    <TableCell className="text-right"><Button variant="ghost" size="sm">Abrir</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {aberta !== null && <DetalheCall id={aberta} onFechar={() => setAberta(null)} />}
    </div>
  );
}

export default function CopilotoInsights() {
  return (
    <PermissionGuard permission="copilot:use">
      <DashboardLayout>
        <CopilotoInsightsContent />
      </DashboardLayout>
    </PermissionGuard>
  );
}
